import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import config from '../config'

const PageSaveContext = createContext()

export const usePageSave = () => {
  const context = useContext(PageSaveContext)
  if (!context) {
    throw new Error('usePageSave must be used within a PageSaveProvider')
  }
  return context
}

export const PageSaveProvider = ({ children }) => {
  const [savedPages, setSavedPages] = useState(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('savedPages')
    return saved ? JSON.parse(saved) : {}
  })

  // Track save timestamps for validation
  const [saveTimestamps, setSaveTimestamps] = useState(() => {
    const saved = localStorage.getItem('saveTimestamps')
    return saved ? JSON.parse(saved) : {}
  })

  // Save to localStorage whenever savedPages changes
  useEffect(() => {
    localStorage.setItem('savedPages', JSON.stringify(savedPages))
  }, [savedPages])

  // Save to localStorage whenever saveTimestamps changes
  useEffect(() => {
    localStorage.setItem('saveTimestamps', JSON.stringify(saveTimestamps))
  }, [saveTimestamps])

  const markPageAsSaved = (pageKey) => {
    // If trying to save any page other than profile, check if profile is saved first
    if (pageKey !== 'profile' && !savedPages['profile']) {
      console.warn(`Cannot save ${pageKey} - Profile must be saved first`)
      return false
    }
    
    // Record the current timestamp for this page save
    const currentTime = Date.now()
    
    setSavedPages(prev => ({
      ...prev,
      [pageKey]: true
    }))
    
    setSaveTimestamps(prev => ({
      ...prev,
      [pageKey]: currentTime
    }))
    
    return true
  }

  const markPageAsUnsaved = (pageKey) => {
    setSavedPages(prev => ({
      ...prev,
      [pageKey]: false
    }))
  }

  const isPageSaved = (pageKey) => {
    return savedPages[pageKey] === true
  }

  const areAllPagesSaved = () => {
    const requiredPages = [
      'profile',
      'salary',
      'filing-section'
    ]

    return requiredPages.every(page => savedPages[page] === true)
  }

  const resetAllPages = () => {
    setSavedPages({})
    setSaveTimestamps({})
  }

  // Validate if any income page was saved after deduction page
  const isDeductionValidationRequired = async () => {
    try {
      // First check API data vs local data for deductions
      const apiValidation = await validateDeductionAgainstAPI()
      if (!apiValidation.isValid) {
        return {
          required: true,
          reason: `Deduction data mismatch detected. Please save deduction page to sync with server.`,
          type: 'api_mismatch',
          mismatches: apiValidation.mismatches
        }
      }

      // Then check timestamp-based validation
      const deductionTimestamp = saveTimestamps['deduction-vi-a']
      if (!deductionTimestamp) {
        return { required: false, reason: null, type: null }
      }

      // Check if any income page was saved after deduction page
      const incomePages = ['salary', 'house-property', 'other-sources']
      const incomePagesSavedAfterDeduction = incomePages.filter(page => {
        const pageTimestamp = saveTimestamps[page]
        return pageTimestamp && pageTimestamp > deductionTimestamp
      })

      if (incomePagesSavedAfterDeduction.length > 0) {
        const pageNames = incomePagesSavedAfterDeduction.map(page => {
          switch(page) {
            case 'salary': return 'Salary'
            case 'house-property': return 'House Property'
            case 'other-sources': return 'Other Sources'
            default: return page
          }
        }).join(', ')

        return {
          required: true,
          reason: `Income pages (${pageNames}) were saved after deduction page. Please re-save deduction page.`,
          type: 'timestamp_mismatch',
          affectedPages: incomePagesSavedAfterDeduction
        }
      }

      return { required: false, reason: null, type: null }
    } catch (error) {
      console.error('Error checking deduction validation:', error)
      return { required: false, reason: null, type: null }
    }
  }

  // Validate deduction data against API
  const validateDeductionAgainstAPI = async () => {
    try {
      const pan = localStorage.getItem('PAN_NO')
      const year = localStorage.getItem('YEAR')
      if (!pan || !year) return { isValid: true, mismatches: [] }

      // Get deduction data from API
      const response = await axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`)
      const apiData = response.data?.taxComputation || {}

      // Get local deduction data
      const localDeductionData = localStorage.getItem('deductionData')
      if (!localDeductionData) return { isValid: true, mismatches: [] }

      const localData = JSON.parse(localDeductionData)
      const mismatches = []

      // Compare total eligible deduction
      const apiTotalDeduction = Number(apiData.totalDeductionVia || 0)
      const localTotalDeduction = Number(localData.totalEligibleDeduction || 0)
      
      if (Math.abs(apiTotalDeduction - localTotalDeduction) > 1) {
        mismatches.push('Total Eligible Deduction')
      }

      // Compare individual deduction sections
      const deductionSections = ['80C', '80D', '80G', '80GGA', '80GGC', '80E', '80EE', '80EEA', '80EEB', '80DD', '80U']
      
      for (const section of deductionSections) {
        const apiValue = Number(apiData[`${section}EligibleDeduction`] || 0)
        const localValue = Number(localData[`${section}EligibleDeduction`] || 0)
        
        if (Math.abs(apiValue - localValue) > 1) {
          mismatches.push(section)
        }
      }

      return {
        isValid: mismatches.length === 0,
        mismatches
      }
    } catch (error) {
      console.error('Error validating deduction against API:', error)
      return { isValid: true, mismatches: [] }
    }
  }

  // Get deduction validation message
  const getDeductionValidationMessage = async () => {
    const validation = await isDeductionValidationRequired()
    return validation
  }

  const value = {
    savedPages,
    markPageAsSaved,
    markPageAsUnsaved,
    isPageSaved,
    areAllPagesSaved,
    resetAllPages,
    validateDeductionAgainstAPI,
    isDeductionValidationRequired,
    getDeductionValidationMessage
  }

  return (
    <PageSaveContext.Provider value={value}>
      {children}
    </PageSaveContext.Provider>
  )
}
